// ============================================================
// ModifierLedger - every active contribution to the next roll,
// as one chip row with a live total and a pre-roll breakdown.
//
// The log always knew the full story; the tab showed an opaque
// "Mod: +30". The ledger closes that gap BEFORE the roll, from the
// SAME functions that feed it (toggleEntries parity, condition
// entries, the user's labelled mods) - the anti-drift rule again.
//
// Chips the user OWNS (stances, custom mods) carry a remove cross;
// derived facts (conditions, range, posture) are information.
// ============================================================
import { useState } from 'react';
import type { ModifierDetail } from '../../engine/structuredNarrative';
import styles from '../../styles/ModifierLedger.module.css';
import clsx from '../../utils/clsx';

interface LedgerChip {
  id: string;
  label: string;
  value: number;
  /** Where it came from - drives the chip's accent. */
  source: 'stance' | 'custom' | 'condition' | 'range' | 'quality';
  /** Present = the user may click the cross to clear it. */
  onRemove?: (() => void) | null;
}

interface Props {
  chips: LedgerChip[];
  total: number;
  /** The full pre-roll table - calcSkillBreakdown's output, base included. */
  breakdown: ModifierDetail[];
  locked?: boolean;
}

const sign = (v: number) => (v > 0 ? `+${v}` : `${v}`);

export default function ModifierLedger({ chips, total, breakdown, locked = false }: Props) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={styles.ledger} data-testid="modifier-ledger">
      <div className={styles.chipRow}>
        <button
          type="button"
          className={styles.total}
          data-testid="ledger-total"
          aria-expanded={expanded}
          onClick={() => setExpanded((e) => !e)}
          title="The net modifier on your next attack - click for the full pre-roll breakdown"
        >
          {sign(total)} <span className={styles.totalCaret}>{expanded ? '▾' : '▸'}</span>
        </button>
        {chips.length === 0 && <span className={styles.empty}>No modifiers active.</span>}
        {chips.map((chip) => (
          <span
            key={chip.id}
            className={clsx(styles.chip, styles[chip.source], chip.value < 0 && styles.negative)}
            title={chip.label}
          >
            {chip.label} {sign(chip.value)}
            {chip.onRemove && !locked && (
              <button
                type="button"
                className={styles.remove}
                aria-label={`Remove ${chip.label}`}
                onClick={chip.onRemove}
              >
                ✕
              </button>
            )}
          </span>
        ))}
      </div>
      {expanded && (
        <table className={styles.breakdown} data-testid="ledger-breakdown">
          <tbody>
            {breakdown.map((m, i) => (
              <tr key={i}>
                <td className={styles.bdLabel}>{m.label}</td>
                <td className={clsx(styles.bdValue, m.value < 0 && styles.negative)}>{sign(m.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
